import * as Sentry from "@sentry/tanstackstart-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "~/components/ui/button";
import { FileUpload } from "~/components/ui/file-upload";
import { Modal } from "~/components/ui/modal/modal";
import { AVATAR_MAX_SIZE_BYTES } from "~/lib/schemas/constants";
import { UserFacingError } from "~/lib/shared/user-facing-error";

interface UploadAvatarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (file: File) => Promise<void>;
}

export const UploadAvatarModal = ({
  isOpen,
  onClose,
  onSave,
}: UploadAvatarModalProps) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // The upload has no real progress signal, so we animate a fake ramp to 90%.
  // Both timers are ref-tracked so they can be cleared on success, failure,
  // close, and unmount — otherwise the interval keeps ticking after a failed
  // upload and the close timeout can fire on an unmounted component.
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Closing the modal abandons whatever upload is in flight, but the promise
  // behind it keeps running. This counter tells a settled upload whether it is
  // still the current one — the component stays mounted between opens, so a
  // late result would otherwise repaint a modal the user has already dismissed
  // and greet them with a stale failure the next time they open it.
  //
  // An abandoned attempt must check this before touching ANY shared state, the
  // timer refs above included: a second upload owns them by then, and clearing
  // its close timeout would strand the modal at 100% with "Saving..." forever.
  const attemptRef = useRef(0);

  const clearTimers = () => {
    if (progressIntervalRef.current !== null) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  useEffect(() => clearTimers, []);

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      setError(null);
      setUploadedFile(files[0]);
    }
  };

  const handleFileError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleUploadSave = async () => {
    if (!uploadedFile) return;

    const attempt = attemptRef.current + 1;
    attemptRef.current = attempt;

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    progressIntervalRef.current = setInterval(() => {
      setUploadProgress((prev) => (prev >= 90 ? 90 : prev + 10));
    }, 200);

    try {
      await onSave(uploadedFile);
      if (attempt !== attemptRef.current) return;

      // Stop the ramp before showing 100% so the bar doesn't jump back to 90%.
      clearTimers();
      setUploadProgress(100);
      closeTimeoutRef.current = setTimeout(handleClose, 500);
    } catch (error) {
      // A `UserFacingError` is something the person can put right — an
      // unfinished field, an expired session. Its message is written for them,
      // and it is not a defect, so it is shown rather than reported. Anything
      // else is a real failure, and stays worth reporting even if the user
      // walked away before it landed.
      if (!(error instanceof UserFacingError)) {
        console.error("Upload failed:", error);
        Sentry.captureException(error, { tags: { surface: "avatar_upload" } });
      }

      if (attempt !== attemptRef.current) return;

      clearTimers();
      setIsUploading(false);
      setUploadProgress(0);
      setError(
        error instanceof UserFacingError
          ? error.message
          : "Something went wrong uploading your photo. Please try again.",
      );
    }
  };

  const handleClose = () => {
    // Abandons any upload still in flight — see attemptRef.
    attemptRef.current += 1;
    clearTimers();
    setUploadedFile(null);
    setIsUploading(false);
    setUploadProgress(0);
    setError(null);
    onClose();
  };

  const handleRemovePhoto = () => {
    setUploadedFile(null);
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      {/* `min-h` rather than a fixed height: the panel renders as a grid whose
          row grows to fit its content, so a fixed height does not clip or
          shrink an error line — it just spills past the panel's background,
          taking the buttons with it. A minimum keeps the usual 473px look and
          lets the box grow by the one line an error adds. */}
      <Modal.Panel size="md" className="min-h-[473px] w-[369px]">
        <div className="flex flex-col gap-4">
          <Modal.Title className="text-2xl font-medium text-gray-900">
            Upload New Avatar
          </Modal.Title>

          {/* A definite height, because both branches below size themselves
              with `h-full` and a percentage needs something to resolve
              against. 320px is what the preview has always been. */}
          <div className="flex h-80 flex-col gap-4">
            {!uploadedFile ? (
              <FileUpload
                onChange={handleFileSelect}
                onError={handleFileError}
                accept="image/jpeg,image/gif,image/png"
                maxSize={AVATAR_MAX_SIZE_BYTES}
                label="Click to upload or drag and drop"
                fullHeight={true}
              />
            ) : (
              <div className="relative h-full overflow-hidden rounded-xl">
                <img
                  src={URL.createObjectURL(uploadedFile)}
                  alt="Preview"
                  className="h-full w-full rounded-xl object-cover"
                />
                {!isUploading && (
                  <Button
                    type="button"
                    className="absolute bottom-5 left-1/2 z-10 -translate-x-1/2 transform rounded-xl bg-white text-sm"
                    onClick={handleRemovePhoto}
                  >
                    Remove photo
                  </Button>
                )}
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="flex flex-col items-center gap-2 rounded-lg bg-white p-4">
                      <div className="h-2 w-32 overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full bg-primary transition-[width] duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 tabular-nums">
                        {uploadProgress}%
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Outside the file area entirely, for two reasons: a failed save
              leaves the preview on screen, so an error rendered only in the
              empty state would be invisible in exactly the case the user needs
              it — and out here it lengthens the panel instead of eating into
              the image. */}
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="flex items-center justify-between">
            <Button type="button" onClick={handleClose} className="text-base">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUploadSave}
              disabled={!uploadedFile || isUploading}
              className="text-base"
            >
              {isUploading ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </Modal.Panel>
    </Modal.Root>
  );
};
